// src/components/sections/Certification/CertificationForm.jsx
import React, { useState, useEffect } from "react";
// reuse EducationForm styles for identical UI
import "../Education/styles/EducationForm.css";

const CertificationForm = ({
  certification = null,
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    issuer: "",
    // month/year UI
    issuedYear: "",
    issuedMonth: "",
    // ISO date for backend
    issuedAt: "",
    licenseId: "",
    isPursuing: false,
    verifierEmail: "",
    registryUrl: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (certification) {
      const issuedDate = certification.issuedAt
        ? certification.issuedAt.split("T")[0]
        : "";

      setFormData({
        name: certification.name || "",
        issuer: certification.issuer || "",
        issuedAt: issuedDate || "",
        issuedYear: issuedDate ? issuedDate.slice(0, 4) : "",
        issuedMonth: issuedDate ? issuedDate.slice(5, 7) : "",
        licenseId: certification.licenseId || "",
        isPursuing: certification.isPursuing || false,
        verifierEmail: certification.verifierEmail || "",
        registryUrl: certification.registryUrl || "",
      });
    } else {
      setFormData({
        name: "",
        issuer: "",
        issuedYear: "",
        issuedMonth: "",
        issuedAt: "",
        licenseId: "",
        isPursuing: false,
        verifierEmail: "",
        registryUrl: "",
      });
    }
    setErrors({});
    setTouched({});
  }, [certification, isOpen]);

  const validateCertification = (data) => {
    const validationErrors = {};

    if (!data.name?.trim()) {
      validationErrors.name = "Certification name is required";
    } else if (data.name.length > 100) {
      validationErrors.name =
        "Certification name must be less than 100 characters";
    }

    if (!data.issuer?.trim()) {
      validationErrors.issuer = "Issuer is required";
    } else if (data.issuer.length > 100) {
      validationErrors.issuer = "Issuer name must be less than 100 characters";
    }

    if (!data.verifierEmail?.trim()) {
      validationErrors.verifierEmail = "Verifier email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.verifierEmail)) {
        validationErrors.verifierEmail = "Invalid email format";
      }
    }

    // Issued date: require both month + year
    if (!data.issuedYear || !data.issuedMonth) {
      validationErrors.issuedAt = "Issued month and year are required";
    } else {
      const issuedDate = new Date(`${data.issuedYear}-${data.issuedMonth}-01`);
      const today = new Date();
      if (issuedDate > today && !data.isPursuing) {
        validationErrors.issuedAt =
          "Issued date cannot be in the future unless pursuing";
      }
    }

    if (data.registryUrl && data.registryUrl.trim()) {
      const urlRegex = /^https?:\/\/.+/;
      if (!urlRegex.test(data.registryUrl)) {
        validationErrors.registryUrl =
          "Registry URL must be a valid URL starting with http:// or https://";
      }
    }

    return validationErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if ((name === "issuedYear" || name === "issuedMonth") && errors.issuedAt) {
      setErrors((prev) => ({ ...prev, issuedAt: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validateCertification(formData);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldErrors[name] || (name === "issuedYear" || name === "issuedMonth"
        ? fieldErrors.issuedAt || ""
        : ""),
      ...(name === "issuedYear" || name === "issuedMonth"
        ? { issuedAt: fieldErrors.issuedAt || "" }
        : {}),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);

    const validationErrors = validateCertification(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      const issuedAt =
        formData.issuedYear && formData.issuedMonth
          ? `${formData.issuedYear}-${formData.issuedMonth}-01`
          : null;

      const submitData = {
        ...formData,
        issuedAt,
        licenseId: formData.licenseId || null,
        registryUrl: formData.registryUrl || null,
      };
      onSubmit(submitData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="edu-modal-overlay">
      <div className="edu-modal">
        {/* Header */}
        <div className="edu-modal-header">
          <div>
            <h2 className="edu-modal-title">
              {certification ? "Edit Certification" : "Add Certification"}
            </h2>
            <p className="edu-modal-subtitle">
              Enter your certification details and verifier email.
            </p>
          </div>
          <button
            type="button"
            className="edu-modal-close"
            onClick={onClose}
            disabled={loading}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form className="edu-form" onSubmit={handleSubmit}>
          {/* Certification details */}
          <section className="form-section">
            <h3>Certification Details</h3>

            <div className="form-group">
              <label>
                Certification name
              </label>
              <input
                type="text"
                name="name"
                className={`edu-input ${touched.name && errors.name ? "error" : ""
                  }`}
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. AWS Certified Solutions Architect"
              />
              {touched.name && errors.name && (
                <div className="edu-error">{errors.name}</div>
              )}
            </div>

            <div className="form-group">
              <label>
                Issuer
              </label>
              <input
                type="text"
                name="issuer"
                className={`edu-input ${touched.issuer && errors.issuer ? "error" : ""
                  }`}
                value={formData.issuer}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. Amazon Web Services"
              />
              {touched.issuer && errors.issuer && (
                <div className="edu-error">{errors.issuer}</div>
              )}
            </div>

            {/* Issued month/year row */}
            <div className="form-row">
              <div className="form-group">
                <label>Issued month</label>
                <select
                  name="issuedMonth"
                  className={`edu-input ${(touched.issuedMonth || touched.issuedYear) &&
                      errors.issuedAt
                      ? "error"
                      : ""
                    }`}
                  value={formData.issuedMonth}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select month</option>
                  {[
                    "01",
                    "02",
                    "03",
                    "04",
                    "05",
                    "06",
                    "07",
                    "08",
                    "09",
                    "10",
                    "11",
                    "12",
                  ].map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, parseInt(m, 10) - 1, 1).toLocaleString(
                        "en-US",
                        { month: "long" }
                      )}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group edu-form-group">
                <label>
                  Issued Year
                </label>
                <select
                  value={formData.issuedYear}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      issuedYear: value,
                      issuedAt:
                        value && prev.issuedMonth
                          ? `${value}-${prev.issuedMonth}-01`
                          : "",
                    }));
                    if (errors.issuedAt) {
                      setErrors((prev) => ({ ...prev, issuedAt: "" }));
                    }
                  }}
                  onBlur={handleBlur}
                  name="issuedYear"
                  className="edu-input select-input"
                >
                  <option value="">Select year</option>
                  {Array.from({ length: 50 }, (_, i) => 1980 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

            </div>
            {(touched.issuedMonth || touched.issuedYear) && errors.issuedAt && (
              <div className="edu-error">{errors.issuedAt}</div>
            )}
          </section>

          {/* Verification & registry */}
          <section className="form-section">
            <h3>Verification & Registry</h3>

            <div className="form-group">
              <label>
                Verifier email <span className="edu-required">*</span>
              </label>
              <input
                type="email"
                name="verifierEmail"
                className={`edu-input ${touched.verifierEmail && errors.verifierEmail ? "error" : ""
                  }`}
                value={formData.verifierEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="verifier@company.com"
              />
              <span className="help-text">
                This email will be used to send verification requests.
              </span>
              {touched.verifierEmail && errors.verifierEmail && (
                <div className="edu-error">{errors.verifierEmail}</div>
              )}
            </div>

            <div className="form-group">
              <label>License ID</label>
              <input
                type="text"
                name="licenseId"
                className="edu-input"
                value={formData.licenseId || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. AWS-1234"
              />
            </div>

            <div className="form-group">
              <label>Registry URL</label>
              <input
                type="url"
                name="registryUrl"
                className={`edu-input ${touched.registryUrl && errors.registryUrl ? "error" : ""
                  }`}
                value={formData.registryUrl || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="https://..."
              />
              {touched.registryUrl && errors.registryUrl && (
                <div className="edu-error">{errors.registryUrl}</div>
              )}
            </div>
          </section>

          {/* Actions */}
          <div className="edu-form-actions">
            <button
              type="button"
              className="edu-btn edu-btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="edu-btn edu-btn-primary"
              disabled={loading}
            >
              {loading && <span className="spinner" />}
              {certification ? "Save changes" : "Save certification"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CertificationForm;
